import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { z } from "zod";

import arrowRightGray from "@/assets/images/user/arrowRightGray.svg";
import icCopy from "@/assets/images/user/icCopy.svg";
import icLink from "@/assets/images/user/icLink.svg";
import InputError from "@/components/console/common/InputError";
import { Dialog, DialogTrigger } from "@/components/ui/dialog";
import TitleBox from "@/components/user/common/TitleBox";
import Checkbox from "@/components/user/form/Checkbox";
import FileUpload, { FileData } from "@/components/user/form/FileUpload";
import Input from "@/components/user/form/Input";
import Radio from "@/components/user/form/Radio";
import Textarea from "@/components/user/form/Textarea";
import { usePostContact } from "@/service/user/contact";
import { usePopupStore } from "@/store/common/usePopupStore";

import TermsPop from "./-components/TermsPop";

const schema = z.object({
    subject: z.string().min(1, "제목을 입력해주세요."),
    organization: z.string().optional(),
    name: z.string().min(1, "성함을 입력해주세요."),
    email: z.string().email("이메일 형식이 올바르지 않습니다."),
    tel: z.string().min(1, "연락처를 입력해주세요."),
    category: z.string().min(1, "카테고리를 선택해주세요."),
    content: z.string().min(1, "내용을 입력해주세요."),
});

type FormValues = z.infer<typeof schema>;

export default function Contact() {
    const {
        register,
        handleSubmit,
        formState: { errors },
        control,
        reset,
    } = useForm<FormValues>({
        resolver: zodResolver(schema),
        defaultValues: {
            subject: "",
            organization: "",
            name: "",
            email: "",
            tel: "",
            category: "게임음악 경연대회 문의",
            content: "",
        },
    });
    const [files, setFiles] = useState<FileData[]>([]);
    const [filesData, setFilesData] = useState<File[]>([]);
    const [termsPop, setTermsPop] = useState(false);
    const [isAgree, setIsAgree] = useState(false);
    const postContactMutation = usePostContact();
    const { setConfirmPop, setLoadingPop } = usePopupStore();

    // 데이터 저장 중일 때 로딩 팝업 표시
    useEffect(() => {
        const isLoading = postContactMutation.isPending;
        setLoadingPop(isLoading);
        return () => setLoadingPop(false);
    }, [postContactMutation.isPending, setLoadingPop]);

    // 이메일 복사
    const handleCopyEmail = () => {
        navigator.clipboard.writeText("info@gxg.world");
        setConfirmPop(true, "이메일이 복사되었습니다.", 1);
    };

    // 문의제출 확인
    const handleConfirmSave = (data: FormValues) => {
        if (!isAgree) {
            setConfirmPop(true, "개인정보 수집방침 이용동의를 체크해주세요.", 1);
        } else {
            setConfirmPop(true, "제출하시겠습니까?", 2, () => onSubmit(data));
        }
    };

    // 문의제출하기
    const onSubmit = (data: FormValues) => {
        const { ...restData } = data;
        const body = {
            ...restData,
            b_file: filesData.length > 0 ? filesData : [],
        };

        postContactMutation.mutate(body, {
            onSuccess: () => {
                setConfirmPop(true, "문의제출이 완료되었습니다.", 1);
                reset();
                setFiles([]);
                setFilesData([]);
                setIsAgree(false);
            },
        });
    };

    return (
        <div className="px-[20px] xl:px-0">
            <section className="mx-auto w-full max-w-[1400px] pb-[120px] md:pb-[160px]">
                <TitleBox title="CONTACT US" text="문의사항" />
                <div className="mt-[28px] rounded-[20px] bg-white p-[16px] md:p-[40px_20px] xl:p-[40px_80px]">
                    <div className="rounded-[20px] bg-[#F9FBFD] p-[20px] text-center font-['GongGothicLight'] md:p-[24px_20px]">
                        <div className="flex flex-col md:flex-row md:items-start md:justify-center md:gap-[16px]">
                            <Link
                                to={`/community/faq`}
                                className="flex items-center justify-center gap-[8px] md:justify-start md:text-[18px]"
                            >
                                <span className="text-primary underline">자주 묻는 질문</span>
                                <img src={icLink} alt="링크" />
                            </Link>
                            <p className="text-black md:text-[18px]">
                                질문으로 원하는 답을 찾지 못하셨다면 문의를 남겨주세요.
                                <br />
                                관련 부서 확인 후 답변 드리겠습니다.
                            </p>
                        </div>
                        <p className="pt-[8px] text-[14px] text-[#666] md:pt-[16px] xl:text-[16px]">
                            <span className="block text-primary md:inline-block">10mb 이상의 대용량 파일 첨부 시</span>{" "}
                            운영사무국 메일로 별도 접수 바랍니다.
                        </p>
                        <button
                            type="button"
                            className="mx-auto mt-[4px] flex h-[30px] items-center gap-[8px] rounded-[20px] px-[12px] font-['GongGothicLight'] text-[14px] text-[#666] shadow-[0_0_4px_0_rgba(124,101,219,0.08)] md:mt-[8px] md:px-[16px]"
                            onClick={handleCopyEmail}
                        >
                            운영사무국 이메일 : <span>info@gxg.world</span>
                            <img src={icCopy} alt="복사" />
                        </button>
                    </div>
                    <form onSubmit={handleSubmit(handleConfirmSave)}>
                        <ul className="flex flex-wrap gap-y-[20px] pt-[20px] md:gap-x-[40px] md:gap-y-[40px] md:pt-[40px] xl:gap-x-[60px]">
                            <li className="flex w-full flex-col gap-[8px] md:flex-row md:items-center">
                                <label htmlFor="subject" className="text-[#666] md:w-[54px]">
                                    제목<span className="pl-[5px] text-[15px] font-[400] text-primary">*</span>
                                </label>
                                <div className="flex-1">
                                    <Input
                                        {...register("subject")}
                                        id="subject"
                                        className="w-full"
                                        placeholder="제목을 입력해주세요."
                                    />
                                    <InputError message={errors.subject?.message} />
                                </div>
                            </li>
                            <li className="flex w-full flex-col gap-[8px] md:w-[calc(50%-20px)] md:flex-row md:items-center xl:w-[calc(50%-30px)]">
                                <label htmlFor="organization" className="text-[#666] md:w-[54px]">
                                    소속
                                </label>
                                <Input
                                    {...register("organization")}
                                    id="organization"
                                    placeholder="소속을 입력해주세요."
                                />
                            </li>
                            <li className="flex w-full flex-col gap-[8px] md:w-[calc(50%-20px)] md:flex-row md:items-center xl:w-[calc(50%-30px)]">
                                <label htmlFor="name" className="text-[#666] md:w-[54px]">
                                    성함<span className="pl-[5px] text-[15px] font-[400] text-primary">*</span>
                                </label>
                                <div className="flex-1">
                                    <Input
                                        {...register("name")}
                                        id="name"
                                        className="w-full"
                                        placeholder="성함을 입력해주세요."
                                    />
                                    <InputError message={errors.name?.message} />
                                </div>
                            </li>
                            <li className="flex w-full flex-col gap-[8px] md:w-[calc(50%-20px)] md:flex-row md:items-center xl:w-[calc(50%-30px)]">
                                <label htmlFor="email" className="text-[#666] md:w-[54px]">
                                    이메일<span className="pl-[5px] text-[15px] font-[400] text-primary">*</span>
                                </label>
                                <div className="flex-1">
                                    <Input
                                        {...register("email")}
                                        id="email"
                                        className="w-full"
                                        placeholder="mail@mail.com"
                                    />
                                    <InputError message={errors.email?.message} />
                                </div>
                            </li>
                            <li className="flex w-full flex-col gap-[8px] md:w-[calc(50%-20px)] md:flex-row md:items-center xl:w-[calc(50%-30px)]">
                                <label htmlFor="tel" className="text-[#666] md:w-[54px]">
                                    연락처<span className="pl-[5px] text-[15px] font-[400] text-primary">*</span>
                                </label>
                                <div className="flex-1">
                                    <Input
                                        {...register("tel")}
                                        id="tel"
                                        className="w-full"
                                        placeholder="연락처를 입력해주세요."
                                    />
                                    <InputError message={errors.tel?.message} />
                                </div>
                            </li>
                            <li className="flex w-full flex-col gap-[8px] md:flex-row">
                                <label htmlFor="tel" className="text-[#666] md:w-[54px]">
                                    구분<span className="pl-[5px] text-[15px] font-[400] text-primary">*</span>
                                </label>
                                <Controller
                                    name="category"
                                    control={control}
                                    render={({ field }) => (
                                        <ul className="flex flex-col gap-[8px] md:flex-row md:flex-wrap xl:flex-1 xl:justify-between xl:gap-0">
                                            <li className="w-full py-[4px] md:w-[calc(50%-4px)] xl:w-1/5">
                                                <Radio
                                                    {...field}
                                                    value="게임음악 경연대회 문의"
                                                    checked={field.value === "게임음악 경연대회 문의"}
                                                    txt="게임음악 경연대회 문의"
                                                />
                                            </li>
                                            <li className="w-full py-[4px] md:w-[calc(50%-4px)] xl:w-1/5">
                                                <Radio
                                                    {...field}
                                                    value="행사 참여 / 일반 문의"
                                                    checked={field.value === "행사 참여 / 일반 문의"}
                                                    txt="행사 참여 / 일반 문의"
                                                />
                                            </li>
                                            <li className="w-full py-[4px] md:w-[calc(50%-4px)] xl:w-1/5">
                                                <Radio
                                                    {...field}
                                                    value="브랜드 협업 제안"
                                                    checked={field.value === "브랜드 협업 제안"}
                                                    txt="브랜드 협업 제안"
                                                />
                                            </li>
                                            <li className="w-full py-[4px] md:w-[calc(50%-4px)] xl:w-1/5">
                                                <Radio
                                                    {...field}
                                                    value="후원 및 기부 문의"
                                                    checked={field.value === "후원 및 기부 문의"}
                                                    txt="후원 및 기부 문의"
                                                />
                                            </li>
                                            <li className="w-full py-[4px] md:w-[calc(50%-4px)] xl:w-1/5">
                                                <Radio
                                                    {...field}
                                                    value="기타"
                                                    checked={field.value === "기타"}
                                                    txt="기타"
                                                />
                                            </li>
                                        </ul>
                                    )}
                                />
                            </li>
                            <li className="flex w-full flex-col gap-[8px] md:flex-row">
                                <label htmlFor="content" className="text-[#666] md:w-[54px] md:pt-[14px]">
                                    내용<span className="pl-[5px] text-[15px] font-[400] text-primary">*</span>
                                </label>
                                <div className="flex-1">
                                    <Textarea
                                        {...register("content")}
                                        id="content"
                                        placeholder="내용을 입력해주세요."
                                    />
                                    <InputError message={errors.content?.message} />
                                </div>
                            </li>
                            <li className="flex w-full flex-col gap-[8px] md:flex-row">
                                <p className="text-[#666] md:w-[54px] md:pt-[14px]">첨부파일</p>
                                <FileUpload
                                    uploadFiles={files}
                                    setFiles={setFiles}
                                    setFilesData={setFilesData}
                                    boxClassName="flex-1"
                                />
                            </li>
                        </ul>
                        <div className="flex flex-col items-center gap-[20px] pt-[20px] md:pt-[40px] xl:gap-[24px]">
                            <div className="flex items-center gap-[12px]">
                                <Checkbox checked={isAgree} onChange={() => setIsAgree(!isAgree)} />
                                <Dialog open={termsPop} onOpenChange={setTermsPop}>
                                    <DialogTrigger asChild>
                                        <button
                                            type="button"
                                            className="flex items-center gap-[12px] text-[#666] md:text-[18px]"
                                        >
                                            (필수) 개인정보 수집방침 이용동의
                                            <img src={arrowRightGray} alt="화살표" />
                                        </button>
                                    </DialogTrigger>
                                    <TermsPop
                                        handleAgree={() => {
                                            setIsAgree(true);
                                            setTermsPop(false);
                                        }}
                                    />
                                </Dialog>
                            </div>
                            <button
                                type="submit"
                                className="relative block h-[60px] w-full max-w-[320px] border border-primary bg-primary text-center font-['GongGothicMedium'] text-[22px] font-[500] leading-[60px] text-white clip-path-octagon xl:h-[72px] xl:leading-[72px]"
                            >
                                <span>문의 제출</span>
                                <span className="absolute left-0 top-0 border-[8px] border-primary border-b-transparent border-r-transparent" />
                                <span className="absolute bottom-0 left-0 border-[8px] border-primary border-r-transparent border-t-transparent" />
                                <span className="absolute right-0 top-0 border-[8px] border-primary border-b-transparent border-l-transparent" />
                                <span className="absolute bottom-0 right-0 border-[8px] border-primary border-l-transparent border-t-transparent" />
                            </button>
                        </div>
                    </form>
                </div>
            </section>
        </div>
    );
}
